import { useCallback, useRef, useState } from "react";
import classNames from "classnames";

import { IPhoneRuleConfig } from "../../../setup/api/services/phoneRuleConfigs/models";
import CountryFlag from "./CountryFlag";
import CountrySheet from "./CountrySheet";
import { useDismissable } from "./useDismissable";
import styles from "./styles.module.scss";

interface Props {
  countries: IPhoneRuleConfig[];
  selectedCountry: IPhoneRuleConfig;
  onSelect: (country: IPhoneRuleConfig) => void;
  disabled?: boolean;
  hasError?: boolean;
}

/** The flag box in front of the phone field; opens the countries sheet. */
const CountrySelector = ({
  countries,
  selectedCountry,
  onSelect,
  disabled,
  hasError,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // With a single allowed country the box is fixed: no chevron and no list.
  const isInteractive = countries.length > 1 && !disabled;

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(containerRef, isOpen, close);

  const handleSelect = useCallback(
    (country: IPhoneRuleConfig) => {
      onSelect(country);
      close();
    },
    [onSelect, close]
  );

  return (
    <div className={styles.selectorContainer} ref={containerRef}>
      <button
        type="button"
        className={classNames(styles.selector, {
          [styles.selectorOpen]: isInteractive && isOpen,
          [styles.selectorFixed]: !isInteractive,
          [styles.fieldError]: hasError,
        })}
        onClick={() => isInteractive && setIsOpen((prev) => !prev)}
        aria-haspopup={isInteractive ? "listbox" : undefined}
        aria-expanded={isInteractive ? isOpen : undefined}
        // Not `disabled`, so the single-country box keeps its normal styling.
        aria-disabled={!isInteractive}>
        <CountryFlag
          emoji={selectedCountry.countryFlag}
          countryName={selectedCountry.countryName}
          size={24}
        />
        {isInteractive && (
          <img
            className={classNames(styles.chevron, {
              [styles.chevronOpen]: isOpen,
            })}
            src="/assets/images/svg/downArrow.svg"
            alt=""
          />
        )}
      </button>

      {isInteractive && isOpen && (
        <CountrySheet
          countries={countries}
          selectedCountryCode={selectedCountry.countryCode}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
};

export default CountrySelector;
